<template>
  <div>
    <GridSkeleton v-if="loading"/>
    <div v-else-if="displayedList.length > 0 || filterText">
      <InputGroup type="text" class="mb-5" :placeholder="$t('message.search.placeholder')"
                  prependIcon="heroicons-outline:search" merged
                  @update:modelValue="searchInOrders"/>
      <div v-if="displayedList.length > 0" class="grid md:grid-cols-4 sm:grid-cols-2 grid-cols-1 gap-5">
        <div v-for="(order, index) in displayedList" :key="index"
             class="bg-white dark:bg-slate-800 p-6 relative z-[1] rounded-2xl text-white flex flex-col justify-between overflow-hidden cursor-pointer"
        >
          <router-link :to="{name: 'manage-applicants-account-order', params:{orderID:order.id}}">
            <div class="w-full">
              <div class="text-xs font-normal">
                <h4 class="text-xl font-medium text-slate-900 dark:text-white mb-2">
                  {{ order.game }}
                </h4>
              </div>
              <div class="widget-title">
                <p class="text-sm text-slate-900 dark:text-white text-opacity-80 break-words">
                  {{ order.title }}
                </p>
              </div>
            </div>
            <div class="mt-6 flex flex-row justify-between">
              <p class="text-success text-sm font-weight-bolder text-lime-600">
                {{ getFormattedDate(order.creationTime) }}
              </p>
              <h6>
                {{ locale === 'fa' ? order.price + ' دلار' : '$' + order.price }}
              </h6>
            </div>
            <div class="absolute left-0 top-0" v-if="order.userApplied">
              <Tooltip
                  placement="top"
                  arrow
              >
                <template v-slot:button>
              <span
                  class="absolute left-0 top-0 text-3xl text-info-600 px-2 py-2 rounded-full inline-block uppercase h-auto opacity-60">
            <Icon icon="mdi:progress-tick"></Icon>
          </span>
                </template>
                <template v-slot:default>
                  {{ $t('message.you reserved this order') }}
                </template>
              </Tooltip>
            </div>
            <img v-if="themeSettingsStore.isDark"
                 src="@/assets/images/svg/line.svg"
                 alt=""
                 class="absolute left-0 bottom-0 w-full z-[-1] opacity-50"
            />
            <img v-else
                 src="@/assets/images/svg/line-dark.svg"
                 alt=""
                 class="absolute left-0 bottom-0 w-full z-[-1] opacity-50"
            />
          </router-link>
        </div>
      </div>
      <Pagination v-if="orders.length / 10 > 1"
                  wrapperClass="bg-slate-100 dark:bg-slate-500 mt-5 w-fit py-2 px-3 rounded mx-auto sticky z-[2] bottom-5"
                  :current="thisPage"
                  :total="orders.length"
                  :per-page="10"
                  @page-changed="doPaginate"
      />
    </div>
    <div v-else
         class="flex justify-center flex-wrap items-center flex-col text-center gap-10"
    >
      <img src="@/assets/images/all-img/no-data1.png" alt=""/>
      <p class="font-normal text-base text-slate-500 dark:text-slate-300">
        {{ $t('message.there is no order to show') }}
      </p>
    </div>
  </div>
</template>

<script>
import {useOrdersStore} from "@/stores/orders.js";
import {useThemeSettingsStore} from "@/stores/themeSettings.js";
import {ref, onMounted} from "vue";
import {useRoute} from "vue-router";
import GridSkeleton from "@/components/Skeleton/Orders.vue";
import Pagination from "@/components/Pagination/index.vue";
import InputGroup from "@/components/InputGroup/index.vue";
import router from "@/router/index.js";
import {computed} from "vue";
import Button from "@/components/Button/index.vue";
import {Icon} from "@iconify/vue";
import Modal from "@/components/Modal/Modal.vue";
import {useI18n} from "vue-i18n";
import jalaliMoment from 'jalali-moment';
import Tooltip from "@/components/Tooltip/index.vue";
import Alert from '@/components/Alert/index.vue';

export default {
  components: {
    Tooltip,
    GridSkeleton,
    Pagination,
    InputGroup,
    Button,
    Icon,
    Modal,
    Alert
  },
  setup() {
    const ordersStore = useOrdersStore();
    const themeSettingsStore = useThemeSettingsStore()
    const route = useRoute();
    const loading = ref(true)
    const orders = ref([])
    const originalOrders = ref([])
    const thisPage = ref(route.query.page !== undefined ? Number(route.query.page) : 1);
    const perPage = 10;
    const thisRoute = route.name;
    const filterText = ref(null);
    const {locale} = useI18n({useScope: 'global'})

    async function getOrders() {
      loading.value = true;
      await ordersStore.action_get_account_active_hot_orders()
      orders.value = ordersStore.getAccountOrders

      originalOrders.value = orders.value
      loading.value = false
    }

    function searchInOrders(emittedValue) {
      filterText.value = emittedValue;

      const filteredData = originalOrders.value.filter(item =>
          Object.values(item).some(value =>
              String(value).toLowerCase().includes(String(emittedValue).toLowerCase())
          )
      );

      if (emittedValue) {
        orders.value = filteredData;
      } else {
        orders.value = originalOrders.value;
      }
    }


    function paginate(list) {
      let from = (thisPage.value * perPage) - perPage;
      let to = (thisPage.value * perPage);
      return list.slice(from, to);
    }

    function doPaginate(pageNumber) {
      thisPage.value = pageNumber;
      router.push({name: thisRoute, query: {page: pageNumber}});
    }

    const getFormattedDate = (timestamp) => {
      const date = new Date(timestamp);
      const jalaliDate = jalaliMoment(date);
      return locale.value === 'fa'
          ? jalaliDate.locale('fa').format('jYYYY/jMM/jDD')
          : jalaliDate.format('YYYY/MM/DD')
    }

    const displayedList = computed(() => paginate(orders.value));

    onMounted(() => {
      getOrders()
    });

    return {
      loading,
      orders,
      displayedList,
      thisPage,
      perPage,
      doPaginate,
      filterText,
      searchInOrders,
      themeSettingsStore,
      locale,
      getFormattedDate
    }
  }
}
</script>

<style scoped>

</style>